import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import '../../styles/admin.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  const { login, setup, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const checkAdminExists = async () => {
      try {
        const apiUrl = getApiUrl();
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${apiUrl}/api/auth/check`, true);
        xhr.setRequestHeader('Accept', 'application/json');
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            setIsSetup(!data.admin_exists);
          } catch (e) {
            console.error('Error parsing auth check response');
          }
          setCheckingSetup(false);
        };
        
        xhr.onerror = function() {
          console.error('Error checking admin');
          setCheckingSetup(false);
        };
        
        xhr.send();
      } catch (error) {
        console.error('Error checking admin:', error);
        setCheckingSetup(false);
      }
    };

    checkAdminExists();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSetup) {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await setup(email, password);
      } else {
        await login(email, password);
      }
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (checkingSetup) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-card">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <h1 className="admin-login-title">The Slay Station</h1>
          <p className="admin-login-subtitle">
            {isSetup ? 'Create Admin Account' : 'Admin Login'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && <div className="admin-error">{error}</div>}

          <div className="admin-input-group">
            <Mail size={20} className="admin-input-icon" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="admin-input"
            />
          </div>

          <div className="admin-input-group">
            <Lock size={20} className="admin-input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="admin-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="admin-password-toggle"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {isSetup && (
            <div className="admin-input-group">
              <Lock size={20} className="admin-input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="admin-input"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="admin-submit-btn"
          >
            {loading ? 'Please wait...' : isSetup ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <div className="admin-login-footer">
          <a href="/" className="admin-back-link">← Back to Website</a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
